import { memo, useEffect, useState, useCallback } from 'react';
import { cn } from '@divlab/divanui';
import { IconReturn } from '@divlab/divanui/icons';

import Link from '@UI/Link';
import useTranslation from '@Queries/useTranslation';
import styles from './ResendCodeTimer.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';

export interface ResendCodeTimerProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  waiting?: boolean;
  seconds: number;
  onEnd?: () => void;
  onRepeat?: (e: MouseEvent) => void;
}

const ResendCodeTimer: FC<ResendCodeTimerProps> = (props) => {
  const { className, waiting, seconds, onEnd, onRepeat, ...restProps } = props;
  const { t } = useTranslation();
  const [leftSeconds, setLeftSeconds] = useState(seconds);
  const finished = leftSeconds < 1;

  const handleRepeat = useCallback(
    (e) => {
      if (waiting) return;

      if (onRepeat) onRepeat(e);
    },
    [onRepeat, waiting],
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setLeftSeconds((prev) => {
        const newSeconds = prev - 1;

        if (newSeconds < 1) {
          if (onEnd) onEnd();

          return 0;
        }

        return newSeconds;
      });
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  });

  return (
    <div {...restProps} className={cn(styles.timer, { [styles.waiting]: waiting }, className)}>
      {finished ? (
        <Link className={styles.action} view='native' onClick={handleRepeat}>
          <IconReturn className={styles.iconReset} />
          {t('ui.resend-code')}
        </Link>
      ) : (
        `${t('ui.resend-code-timeout')} ${leftSeconds} ${t('ui.plural-seconds', leftSeconds)}`
      )}
    </div>
  );
};

export default memo(ResendCodeTimer);
